import React, { useEffect, useState } from "react";
import {
  EMBED_ALLOW,
  EMBED_LOAD_TIMEOUT_MS,
  EMBED_REFERRER_POLICY,
  EMBED_SANDBOX,
  aspectPadding,
  isSafeEmbedUrl,
} from "./embedPolicy";

/**
 * Embedded third-party game in a sandboxed frame with a fallback when the
 * frame does not load (blocked, refused or offline). Browsers fire onLoad
 * even for a frame the provider refuses (X-Frame-Options), so an
 * open-in-new-tab link is always shown as well.
 */
function EmbeddedGame({ embedUrl, aspectRatio, title }) {
  const appOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const safe = isSafeEmbedUrl(embedUrl, appOrigin);
  const [loaded, setLoaded] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setTimedOut(false);
    if (!safe) return undefined;
    const timer = window.setTimeout(() => setTimedOut(true), EMBED_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [embedUrl, safe]);

  if (!safe) {
    return (
      <div className="games-panel games-panel--error" role="alert">
        <h2 className="games-panel__title">This game is unavailable</h2>
        <p className="games-panel__body">It can’t be shown here right now.</p>
      </div>
    );
  }

  return (
    <div className="games-embed">
      <div className="games-embed__frame" style={{ paddingTop: `${aspectPadding(aspectRatio)}%` }}>
        <iframe
          src={embedUrl}
          title={title || "Embedded game"}
          sandbox={EMBED_SANDBOX}
          allow={EMBED_ALLOW}
          referrerPolicy={EMBED_REFERRER_POLICY}
          loading="lazy"
          onLoad={() => setLoaded(true)}
        />
        {!loaded && !timedOut ? (
          <div className="games-embed__loading" role="status" aria-live="polite" data-testid="games-embed-loading">
            Loading game…
          </div>
        ) : null}
      </div>
      <p className="games-embed__fallback">
        Game not showing?{" "}
        <a href={embedUrl} target="_blank" rel="noopener noreferrer">
          Open it in a new tab
        </a>
      </p>
      {!loaded && timedOut ? (
        <div className="games-panel games-panel--error" role="alert">
          <h2 className="games-panel__title">The game is taking too long to load</h2>
          <p className="games-panel__body">
            The provider may be blocking embedding or be temporarily unavailable.
          </p>
          <a
            className="btn btn-outline-secondary"
            href={embedUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open the game in a new tab
          </a>
        </div>
      ) : null}
    </div>
  );
}

export default EmbeddedGame;
